import { SeasonContext, SeasonListItem, SeasonName, SeasonTypeCode } from '../types/Season';

const seasonTypeToName: Record<SeasonTypeCode, SeasonName> = {
  SPR: SeasonName.Spring,
  SUM: SeasonName.Summer,
  FAL: SeasonName.Fall,
  WNT: SeasonName.Winter,
  SAF: SeasonName.Safrinha,
};

const seasonNameAbbrByName: Record<SeasonName, SeasonTypeCode> = {
  [SeasonName.Spring]: 'SPR',
  [SeasonName.Summer]: 'SUM',
  [SeasonName.Fall]: 'FAL',
  [SeasonName.Winter]: 'WNT',
  [SeasonName.Safrinha]: 'SAF'
};

export const getSeasonNameFromType = (type: SeasonTypeCode): SeasonName =>
  seasonTypeToName[type];

export const getSeasonTypeCodeFromTag = (seasonTag: string): SeasonTypeCode =>
  seasonTag.split(':').filter(Boolean).pop() as SeasonTypeCode;

export const resolveSeasonYear = (season: SeasonListItem | undefined, seasonTag: string): number => {
  if (season?.year) {
    return season.year;
  }

  return Number.parseInt(seasonTag.split(':')[0], 10);
};

export const parseSeasonTag = (
  seasonTag: string,
  season?: SeasonListItem,
): SeasonContext => ({
  seasonTag,
  seasonName: getSeasonNameFromType(getSeasonTypeCodeFromTag(seasonTag)),
  year: resolveSeasonYear(season, seasonTag),
});

export const createSeasonContexts = (
  seasons: Record<string, SeasonListItem>,
  activeSeasons: Array<{ id?: string | null }>,
): SeasonContext[] => {
  const tags = new Set<string>(Object.keys(seasons));
  activeSeasons.forEach(({ id }) => {
    if (id) {
      tags.add(id);
    }
  });

  const contexts: SeasonContext[] = [];
  tags.forEach((seasonTag) => {
    const context = parseSeasonTag(seasonTag, seasons[seasonTag]);
    contexts.push(context);
  });

  return contexts;
};

export function getSeasonTagWithWildcards(seasonTag: string): string {
  const fragments = seasonTag.split(':');

  if (fragments.length === 5) {
    return buildSeasonTagWithWildcards({
      year: Number.parseInt(fragments[0], 10),
      cropCode: fragments[1],
      season: fragments[4],
    });
  }

  return seasonTag;
}

export function isSeasonTagWithWildcards(seasonTag: string): boolean {
  return seasonTag.includes('*');
}

export interface SeasonTagComponents {
  year: number;
  cropCode?: string;
  season: SeasonTypeCode | string;
}
export function buildSeasonTagWithWildcards({year, cropCode, season}: SeasonTagComponents): string {
  const fragments: string[] = [year.toString()];

  if(cropCode) {
    fragments.push(cropCode);
  } else {
    fragments.push('*');
  }

  const seasonFragment = season.length === 3 ? season : seasonNameAbbrByName[season];

  fragments.push('*'); // wildcard for geography code
  fragments.push('*'); // wildcard for sequence number
  fragments.push(seasonFragment);

  return fragments.join(':');
}